import React from 'react';
import { Link } from 'react-router-dom';
import { useCompetencyCourses } from '../hooks/useCompetency';

const CompetencyCourses = () => {
  const { courses, loading, error } = useCompetencyCourses('published');

  if (loading) {
    return (
      <div className="container">
        <h1>Professional Competency Courses</h1>
        <p>Loading courses...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container">
        <h1>Professional Competency Courses</h1>
        <div style={{ padding: '20px', background: '#fee', border: '1px solid #fcc', borderRadius: '4px' }}>
          <strong>Error:</strong> {error}
        </div>
      </div>
    );
  }

  const competencyCategories = {
    'Business & Management': ['management', 'negotiation', 'business_growth', 'profit_loss'],
    'Operations & Logistics': ['transport', 'stock_control', 'warehouse_skills'],
    'Professional Skills': ['administrative', 'engineering', 'driving']
  };

  const getCategoryForType = (type) => {
    for (const [category, types] of Object.entries(competencyCategories)) {
      if (types.includes(type)) return category;
    }
    return 'Other';
  };

  const groupedCourses = courses.reduce((acc, course) => {
    const category = getCategoryForType(course.competency_type);
    if (!acc[category]) acc[category] = [];
    acc[category].push(course);
    return acc;
  }, {});

  return (
    <div className="container">
      <div style={{ marginBottom: '30px' }}>
        <h1>ICSC Professional Competency Accreditation Framework (PCAF)</h1>
        <p style={{ fontSize: '16px', color: '#666', marginBottom: '20px' }}>
          Achieve internationally recognized professional competency qualifications.
          Complete courses, pass assessments and exams, and earn ICSC certificates.
        </p>

        <div style={{
          padding: '15px',
          background: '#e3f2fd',
          border: '1px solid #2196f3',
          borderRadius: '4px',
          marginBottom: '30px'
        }}>
          <strong>How it works:</strong> Enroll in a course → Complete modules → Pass assessment → Pass exam → Submit evidence → Get accredited → Receive certificate (£50 fee)
        </div>
      </div>

      {Object.keys(groupedCourses).length === 0 ? (
        <div style={{ padding: '40px', textAlign: 'center', background: '#f5f5f5', borderRadius: '4px' }}>
          <p>No courses available yet. Check back soon!</p>
        </div>
      ) : (
        Object.entries(groupedCourses).map(([category, categoryCourses]) => (
          <div key={category} style={{ marginBottom: '40px' }}>
            <h2 style={{
              borderBottom: '2px solid #D4AF37',
              paddingBottom: '10px',
              color: '#333',
              marginBottom: '20px'
            }}>
              {category}
            </h2>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
              gap: '20px'
            }}>
              {categoryCourses.map(course => (
                <Link
                  key={course.id}
                  to={`/competency/courses/${course.id}`}
                  style={{ textDecoration: 'none' }}
                >
                  <div style={{
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    padding: '20px',
                    background: '#fff',
                    transition: 'all 0.3s',
                    cursor: 'pointer',
                    height: '100%',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                    ':hover': {
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                      borderColor: '#D4AF37'
                    }
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
                    e.currentTarget.style.borderColor = '#D4AF37';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.05)';
                    e.currentTarget.style.borderColor = '#ddd';
                  }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '10px' }}>
                      <h3 style={{ margin: 0, color: '#333', fontSize: '18px' }}>
                        {course.title}
                      </h3>
                      <div style={{
                        background: '#D4AF37',
                        color: '#fff',
                        padding: '4px 10px',
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: 'bold',
                        whiteSpace: 'nowrap',
                        marginLeft: '10px'
                      }}>
                        {course.cpd_hours} CPD Hours
                      </div>
                    </div>

                    <p style={{ color: '#666', fontSize: '14px', lineHeight: '1.6', marginBottom: '15px' }}>
                      {course.description || 'Professional competency qualification'}
                    </p>

                    <div style={{
                      display: 'flex',
                      gap: '15px',
                      fontSize: '13px',
                      color: '#888',
                      marginBottom: '15px',
                      paddingTop: '15px',
                      borderTop: '1px solid #eee'
                    }}>
                      <div>
                        <strong style={{ color: '#666' }}>{course.total_modules}</strong> Modules
                      </div>
                      <div>
                        <strong style={{ color: '#666' }}>{course.estimated_duration_hours}h</strong> Duration
                      </div>
                      <div>
                        <strong style={{ color: '#666' }}>£{course.certificate_fee}</strong> Certificate
                      </div>
                    </div>

                    <div style={{
                      background: '#f8f9fa',
                      padding: '10px',
                      borderRadius: '4px',
                      fontSize: '12px',
                      color: '#666'
                    }}>
                      <strong>Code:</strong> {course.course_code}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        ))
      )}

      <div style={{
        marginTop: '40px',
        padding: '20px',
        background: '#fff9e6',
        border: '1px solid #D4AF37',
        borderRadius: '4px'
      }}>
        <h3 style={{ marginTop: 0 }}>Certificate Pricing</h3>
        <ul style={{ marginBottom: 0 }}>
          <li><strong>No enrollment fees</strong> - Start learning for free</li>
          <li><strong>£50 per certificate</strong> - Only pay when you successfully complete</li>
          <li><strong>3-year validity</strong> - Certificates valid for 36 months</li>
          <li><strong>CPD hours awarded</strong> - Typically 20 hours per qualification</li>
        </ul>
      </div>
    </div>
  );
};

export default CompetencyCourses;
